// Access control readers admin CRUD — SPA client for
// /api/buildings/:id/readers* (spec §10). Mirrors the shape/style of
// apps/admin/src/api/rooms.ts: DTOs come from @hub-wifi/shared so the SPA
// and API can't drift; every function takes the Auth0 access `token`
// explicitly, threaded last to match rooms.ts call sites.
import type { AccessReaderDto, AccessReaderStatus, AccessVendor } from '@hub-wifi/shared'
import { apiBlob, apiRequest, ApiError } from './client'

export type { AccessReaderDto, AccessReaderStatus, AccessVendor }

export interface CreateAccessReaderInput {
  name: string
  vendor: AccessVendor
  config: Record<string, unknown>
}

export interface UpdateAccessReaderInput {
  name?: string
  status?: AccessReaderStatus
  config?: Record<string, unknown>
}

/** POST create returns the created reader plus a one-time device token — controlid only, 43 chars, shown once. */
export interface CreateAccessReaderResult {
  reader: AccessReaderDto
  deviceToken?: string
}

export function listAccessReaders(buildingId: string, token: string): Promise<AccessReaderDto[]> {
  return apiRequest<AccessReaderDto[]>(`/api/buildings/${buildingId}/readers`, { token })
}

export function createAccessReader(
  buildingId: string,
  input: CreateAccessReaderInput,
  token: string,
): Promise<CreateAccessReaderResult> {
  return apiRequest<CreateAccessReaderResult>(`/api/buildings/${buildingId}/readers`, {
    method: 'POST',
    body: input,
    token,
  })
}

export function updateAccessReader(
  buildingId: string,
  readerId: string,
  patch: UpdateAccessReaderInput,
  token: string,
): Promise<AccessReaderDto> {
  return apiRequest<AccessReaderDto>(`/api/buildings/${buildingId}/readers/${readerId}`, {
    method: 'PUT',
    body: patch,
    token,
  })
}

export function deleteAccessReader(
  buildingId: string,
  readerId: string,
  token: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/api/buildings/${buildingId}/readers/${readerId}`, {
    method: 'DELETE',
    token,
  })
}

/** One ControlID device user, reconciled against hub coworkers (Task 6b). */
export interface ReaderDeviceUser {
  deviceUserId: number
  deviceName: string
  mapped: boolean
  suggestedCoworkerId: string | null
}

export interface ListReaderDeviceUsersResult {
  deviceUsers: ReaderDeviceUser[]
  /** The reader's building's coworkers — populates the mapping Select without a second call. */
  coworkers: { id: string; name: string }[]
}

/** controlid-only; truesafe readers (or unknown ids) 404 with READER_NOT_FOUND. */
export function listReaderDeviceUsers(
  buildingId: string,
  readerId: string,
  token: string,
): Promise<ListReaderDeviceUsersResult> {
  return apiRequest<ListReaderDeviceUsersResult>(
    `/api/buildings/${buildingId}/readers/${readerId}/device-users`,
    { token },
  )
}

/** Manual full directory resync for one controlid reader (Task 9/14 row action). */
export function resyncAccessReader(
  buildingId: string,
  readerId: string,
  token: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/api/buildings/${buildingId}/readers/${readerId}/resync`, {
    method: 'POST',
    token,
  })
}

/**
 * Fetches one device user's enrolled face/photo as a Blob, or null if the
 * device user has no image on file (404). Mirrors facePhotos.ts's
 * getFacePhoto: the endpoint is admin-gated, so a bare `<img src>` would
 * 401 — callers must fetch-with-auth via this fn and then hand the browser
 * an object URL (URL.createObjectURL).
 */
export async function getReaderDeviceUserImage(
  buildingId: string,
  readerId: string,
  deviceUserId: number,
  token: string,
): Promise<Blob | null> {
  try {
    return await apiBlob(
      `/api/buildings/${buildingId}/readers/${readerId}/device-users/${deviceUserId}/image`,
      { token },
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

export function mapReaderDeviceUser(
  buildingId: string,
  readerId: string,
  deviceUserId: number,
  coworkerId: string,
  token: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/buildings/${buildingId}/readers/${readerId}/device-users/${deviceUserId}/map`,
    { method: 'POST', body: { coworkerId }, token },
  )
}
